import React, { useEffect, useState } from "react";
import {
  AlertTriangle, Briefcase, Camera, ChevronRight, Eye, EyeOff, GraduationCap,
  KeyRound, LogIn, LogOut, Mic, Settings2, Wallet,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, clearApiAuth, saveApiSettings } from "../Api/Client.js";
import { returnAddress } from "../Api/returnAddress.js";
import { companyBrandName } from "../Shared/companyBrand.js";
import { formatMoney } from "../Shared/money.js";
import { latestPayProfile, resolveActiveEmployee } from "../Shared/ScreenUtils.jsx";
import VoiceRecorder from "../Components/VoiceRecorder.jsx";
import ChangeOwnPassword from "../Components/ChangeOwnPassword.jsx";
import { ResignationSection } from "../Products/people/index.js";
import { DEFAULT_LOGO as atgLogoPng } from "../Components/icons/icons.jsx";
import "../Styles/ProfileScreen.css";
import "../Styles/LoginScreen.css";

export function LoginScreen({ settings, onLogin }) {
  const [form, setForm] = useState({
    apiBase: settings.apiBase,
    tenantId: settings.tenantId || "1",
    workspaceId: settings.workspaceId || "1",
    identifier: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm((current) => ({
      ...current,
      apiBase: settings.apiBase,
      tenantId: settings.tenantId || "1",
      workspaceId: settings.workspaceId || "1",
    }));
  }, [settings]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.identifier || !form.password) {
      setError("Enter Your Username And Password.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const loginSettings = {
        apiBase: form.apiBase,
        tenantId: form.tenantId,
        workspaceId: form.workspaceId,
      };
      saveApiSettings(loginSettings);
      // The password goes to the server once; the reply sets the session cookie that every
      // later call carries. Nothing of it stays in the browser (W06-F01).
      const currentUser = await apiPost("/Users/Auth/Login/", {
        identifier: form.identifier,
        // Keep the legacy field during rolling deployments. Old backends use
        // username; Identity V2 prefers identifier.
        username: form.identifier,
        password: form.password,
        tenant_id: form.tenantId ? parseInt(form.tenantId, 10) || null : null,
        workspace_id: form.workspaceId ? parseInt(form.workspaceId, 10) || null : null,
      });
      saveApiSettings({
        ...loginSettings,
        signedIn: true,
        tenantId: currentUser?.activeTenant?.id || form.tenantId,
        workspaceId: currentUser?.activeWorkspace?.id || form.workspaceId,
      });
      // Sent here by another app's sign-in step: go back to it instead of home.
      const back = returnAddress(window.location.search, window.location.origin);
      if (back) {
        window.location.assign(back);
        return;
      }
      if (!currentUser.user?.isSuperuser) {
        const employee = currentUser.employees?.[0];
        if (employee) {
          const employeeProfile = await apiGet(`/Users/EmployeeProfiles/${employee.id}/`);
          if (!employeeProfile.onboarding_completed) {
            onLogin("/onboarding/");
            return;
          }
        }
      }
      onLogin();
    } catch (loginError) {
      clearApiAuth();
      setError(loginError?.status === 401 ? "Invalid Username Or Password." : loginError?.message || "Login Failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="Login-Page">
      <div className="Login-Bg" />

      <div className="Login-Container">
        <div className="Login-Brand">
          <div className="Login-Logo">
            <img src={atgLogoPng} alt="Banao Technologies" width="48" height="48" />
          </div>
          <h1>Intranet</h1>
          <p>Welcome Back — Sign In To Your Workspace</p>
        </div>

        <form className="Login-Card" onSubmit={submit}>
          {error && (
            <div className="Login-Error">
              <AlertTriangle size={15} />
              <span>{error}</span>
            </div>
          )}

          <div className="Login-Field">
            <label>Username</label>
            <div className="Login-Input-Wrap">
              <input autoFocus value={form.identifier} onChange={(e) => update("identifier", e.target.value)} autoComplete="username" placeholder="Enter your username or account" />
            </div>
          </div>

          <div className="Login-Field">
            <label>Password</label>
            <div className="Login-Input-Wrap">
              <input type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => update("password", e.target.value)} autoComplete="current-password" placeholder="Enter Your Password" />
              <button type="button" className="Login-Pw-Toggle" onClick={() => setShowPassword((v) => !v)} title={showPassword ? "Hide" : "Show"}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button className="Login-Submit" type="submit" disabled={submitting}>
            {submitting ? (
              <span className="Login-Spinner" />
            ) : (
              <LogIn size={18} />
            )}
            <span>{submitting ? "Signing In..." : "Sign In"}</span>
          </button>
        </form>

      </div>
    </main>
  );
}

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const MAX_AVATAR_DIMENSION = 512;

// Resized and re-encoded client-side purely so a slow connection isn't
// stuck uploading a 12MB phone photo — the server re-does all of this
// itself (Apps/Users/views.py's "avatar" action) and is the real limit,
// not this.
function compressAvatarImage(file) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > height && width > MAX_AVATAR_DIMENSION) {
        height = Math.round((height * MAX_AVATAR_DIMENSION) / width);
        width = MAX_AVATAR_DIMENSION;
      } else if (height > MAX_AVATAR_DIMENSION) {
        width = Math.round((width * MAX_AVATAR_DIMENSION) / height);
        height = MAX_AVATAR_DIMENSION;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Could not process that image."))),
        "image/jpeg",
        0.82
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that file.")); };
    img.src = url;
  });
}

// One collapsible card. Sections someone looks at often (their own contact
// details, the facts about their role) open by default; set-once-at-
// onboarding or rarely-needed ones (education, pay, account/session
// technicalia) start collapsed, one click away instead of permanently
// taking up space on every visit.
function PSection({ title, subtitle, icon, tone, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className={"PProfile-Section" + (open ? " open" : "")}>
      <button type="button" className="PProfile-Section-Head" onClick={() => setOpen((v) => !v)}>
        <span className="PProfile-Section-Head-Left">
          <span className={"PProfile-Section-Icon " + (tone || "")}>{icon}</span>
          <span className="PProfile-Section-Titles">
            <h3>{title}</h3>
            <span className="sub">{subtitle}</span>
          </span>
        </span>
        <ChevronRight size={15} className="PProfile-Chevron" />
      </button>
      <div className="PProfile-Section-Body"><div className="PProfile-Section-Body-Inner">{children}</div></div>
    </div>
  );
}

function PField({ label, children, span2 }) {
  return (
    <div className={"PProfile-Field" + (span2 ? " span-2" : "")}>
      <span className="f-label">{label}</span>
      {children}
    </div>
  );
}

export function ProfileScreen({ data, onLogout, reload, navigate }) {
  const user = data.me?.user || data.me?.account || data.me || {};
  const employee = resolveActiveEmployee(data) || {};
  const fullName = employee.display_name || employee.displayName || user.fullName || user.full_name || user.username || "User";
  const initials = String(fullName).split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "U";
  const payProfile = latestPayProfile(data.payProfiles, employee.id);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { setAvatarError("Pick an image file."); return; }
    if (file.size > MAX_AVATAR_BYTES) { setAvatarError("That file is over 5MB."); return; }
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const compressed = await compressAvatarImage(file);
      const formData = new FormData();
      formData.append("avatar", compressed, "avatar.jpg");
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/avatar/`, formData);
      if (reload) reload(["employees", "me"]);
    } catch (err) {
      setAvatarError(err?.payload?.detail || err?.message || "Could not update your photo.");
    } finally {
      setAvatarBusy(false);
    }
  };

  // Contact & Personal — the fields someone actually comes here to change.
  const [contactForm, setContactForm] = useState({
    display_name: employee.display_name || "",
    contact_number: employee.contact_number || "",
    city: employee.city || "",
    address: employee.profile_payload?.address || "",
    emergency_contact: employee.profile_payload?.emergency_contact || "",
  });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const saveContact = async () => {
    setContactBusy(true);
    setContactError("");
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        display_name: contactForm.display_name,
        contact_number: contactForm.contact_number,
        city: contactForm.city,
      });
      // address/emergency_contact live inside the free-form profile_payload
      // blob, which the generic PATCH above deliberately never allows (it's
      // not a single field, it's a bag HR also writes to) — merged through
      // the dedicated patch-payload action instead.
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/patch-payload/`, {
        profile_payload: { address: contactForm.address, emergency_contact: contactForm.emergency_contact },
      });
      if (reload) reload(["employees", "me"]);
    } catch (err) {
      setContactError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setContactBusy(false);
    }
  };

  // Integrations & Tools — GitHub/Slack/Calendar/weekly hours.
  const [toolsForm, setToolsForm] = useState({
    github_username: employee.github_username || "",
    slack_username: employee.slack_username || "",
    calendar_id: employee.calendar_id || "",
    availability_hours: employee.availability_hours || 40,
  });
  const [toolsBusy, setToolsBusy] = useState(false);
  const [toolsError, setToolsError] = useState("");
  const saveTools = async () => {
    setToolsBusy(true);
    setToolsError("");
    try {
      // People often paste their Slack profile LINK (.../team/U01AB2CDE3F); what is
      // stored is the member ID at the end of it.
      const slackMatch = String(toolsForm.slack_username || "").match(/slack\.com\/team\/([A-Z0-9]+)/i);
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        github_username: toolsForm.github_username,
        slack_username: slackMatch ? slackMatch[1].toUpperCase() : toolsForm.slack_username,
        calendar_id: toolsForm.calendar_id,
        availability_hours: Number(toolsForm.availability_hours) || 40,
      });
      if (reload) reload(["employees", "me"]);
    } catch (err) {
      setToolsError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setToolsBusy(false);
    }
  };

  // Education — set once at onboarding, so its own small section rather
  // than mixed in with things people actually revisit.
  const [eduForm, setEduForm] = useState({
    college_name: employee.college_name || "",
    year_of_graduation: employee.year_of_graduation || "",
  });
  const [eduBusy, setEduBusy] = useState(false);
  const [eduError, setEduError] = useState("");
  const saveEdu = async () => {
    setEduBusy(true);
    setEduError("");
    try {
      await apiPatch(`/Users/EmployeeProfiles/${employee.id}/`, {
        college_name: eduForm.college_name,
        year_of_graduation: Number(eduForm.year_of_graduation) || null,
      });
      if (reload) reload(["employees", "me"]);
    } catch (err) {
      setEduError(err?.payload?.detail || err?.message || "Could not save those changes.");
    } finally {
      setEduBusy(false);
    }
  };

  // "Getting started": the few facts the rest of the system quietly depends on.
  // The list and the reasons come from the server -- the same list the daily
  // reminder counts -- so the page can never say "done" while the reminder
  // still disagrees. Re-read after each save and after a voice sample changes.
  const [setup, setSetup] = useState(null);
  const refreshSetup = () => {
    apiGet("/Users/EmployeeProfiles/setup-checklist/").then(setSetup).catch(() => {});
  };
  useEffect(() => { refreshSetup(); }, [employee.id, employee.contact_number, employee.github_username, employee.slack_username]);

  const joinedLabel = employee.joined_on
    ? new Date(employee.joined_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

  return (
    <section className="PProfile">
      <section className="PProfile-Hero">
        <div className="PProfile-Avatar-Wrap">
          <div className="PProfile-Avatar">
            {employee.avatar_url ? <img src={employee.avatar_url} alt="" /> : initials}
          </div>
          <label className="PProfile-Avatar-Edit" title="Change photo">
            <Camera size={13} />
            <input type="file" accept="image/png, image/jpeg, image/webp" onChange={onAvatarChange} disabled={avatarBusy} />
          </label>
        </div>
        <div className="PProfile-Info">
          <h1>{fullName}</h1>
          <p>{employee.department_name || companyBrandName(data.me?.company)} &middot; {employee.position_title || "Intranet User"}</p>
          {avatarError && <p className="PProfile-Avatar-Error">{avatarError}</p>}
        </div>
        <div className="PProfile-Actions">
          <button className="PProfile-Btn" onClick={onLogout}><LogOut size={15} /> Logout</button>
        </div>
      </section>

      <div className="PProfile-QuickFacts">
        <div className="PProfile-QuickFact"><span className="label">Department</span><span className="value">{employee.department_name || "-"}</span></div>
        <div className="PProfile-QuickFact"><span className="label">Employment Type</span><span className="value">{employee.employment_type || "-"}</span></div>
        <div className="PProfile-QuickFact"><span className="label">Manager</span><span className="value">{employee.manager_name || "-"}</span></div>
        <div className="PProfile-QuickFact"><span className="label">Joined</span><span className="value">{joinedLabel}</span></div>
      </div>

      {setup && setup.remaining > 0 && (
        <section className="PProfile-Setup">
          <h2>Getting started <span>{setup.remaining} of {setup.items.length} left</span></h2>
          <ul>
            {setup.items.map((item) => (
              <li key={item.key} className={item.done ? "done" : ""}>
                <span className="mark" aria-hidden="true">{item.done ? "✓" : ""}</span>
                <div>
                  <strong>{item.label}</strong>
                  {!item.done && <p>{item.why}</p>}
                  {!item.done && item.hint && <p className="hint">{item.hint}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PSection title="Contact & Personal" subtitle="Editable — reach you, find you" icon="◐" tone="blue" defaultOpen>
        {contactError && <div className="Login-Error"><AlertTriangle size={14} /> {contactError}</div>}
        <div className="PProfile-Field-Grid">
          <PField label="Display Name"><input value={contactForm.display_name} onChange={(e) => setContactForm({ ...contactForm, display_name: e.target.value })} /></PField>
          <PField label="Contact Number"><input value={contactForm.contact_number} onChange={(e) => setContactForm({ ...contactForm, contact_number: e.target.value })} /></PField>
          <PField label="Personal Email"><span className="f-value muted">{user.email || employee.email || "-"}</span></PField>
          <PField label="City"><input value={contactForm.city} onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })} /></PField>
          <PField label="Address" span2><textarea rows={2} value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} /></PField>
          <PField label="Emergency Contact" span2><input value={contactForm.emergency_contact} onChange={(e) => setContactForm({ ...contactForm, emergency_contact: e.target.value })} /></PField>
        </div>
        <div className="PProfile-Section-Actions">
          <button className="PProfile-Save-Btn" onClick={saveContact} disabled={contactBusy}>{contactBusy ? "Saving…" : "Save changes"}</button>
        </div>
      </PSection>

      <PSection title="Work" subtitle="Set by HR — not yours to edit here" icon={<Briefcase size={15} />} tone="amber" defaultOpen>
        <div className="PProfile-Field-Grid">
          <PField label="Position"><span className="f-value">{employee.position_title || "-"}</span></PField>
          <PField label="Manager"><span className="f-value">{employee.manager_name || "-"}</span></PField>
          <PField label="Employment Type"><span className="f-value">{employee.employment_type || "-"}</span></PField>
          <PField label="Status"><span className="f-value">{employee.status || "-"}</span></PField>
          <PField label="Joined"><span className="f-value">{joinedLabel}</span></PField>
          <PField label="Employee Code"><span className="f-value">{employee.employee_code || "-"}</span></PField>
        </div>
      </PSection>

      <PSection title="Integrations & Tools" subtitle="GitHub, Slack, Calendar, weekly hours" icon={<Settings2 size={15} />}>
        {toolsError && <div className="Login-Error"><AlertTriangle size={14} /> {toolsError}</div>}
        <div className="PProfile-Field-Grid">
          <PField label="GitHub Username"><input value={toolsForm.github_username} onChange={(e) => setToolsForm({ ...toolsForm, github_username: e.target.value })} />
            <small className="PProfile-Hint">Only needed if you are in product engineering and push code.</small></PField>
          <PField label="Slack member ID"><input value={toolsForm.slack_username} onChange={(e) => setToolsForm({ ...toolsForm, slack_username: e.target.value })} placeholder="U01AB2CDE3F" />
            <small className="PProfile-Hint">In Slack: click your name, then the three dots, then Copy member ID. Pasting your profile link works too.</small></PField>
          <PField label="Calendar ID"><input value={toolsForm.calendar_id} onChange={(e) => setToolsForm({ ...toolsForm, calendar_id: e.target.value })} /></PField>
          <PField label="Availability (hrs/week)"><input type="number" min="0" max="168" value={toolsForm.availability_hours} onChange={(e) => setToolsForm({ ...toolsForm, availability_hours: e.target.value })} /></PField>
        </div>
        <div className="PProfile-Section-Actions">
          <button className="PProfile-Save-Btn" onClick={saveTools} disabled={toolsBusy}>{toolsBusy ? "Saving…" : "Save changes"}</button>
        </div>
      </PSection>

      <PSection title="Voice sample" subtitle="Lets the meeting recorder know it is you speaking" icon={<Mic size={15} />}>
        <p className="PProfile-Why">
          The meeting recorder labels who said what. With a short sample of your voice it can credit your
          words to you; without one, your part of a recorded meeting shows up as an unknown speaker.
        </p>
        <div className="PProfile-VoiceWrap">
          <VoiceRecorder employeeId={employee.id} employeeName={fullName} onChange={refreshSetup} />
        </div>
      </PSection>

      <PSection title="Education" subtitle="Filled in once at onboarding" icon={<GraduationCap size={15} />}>
        {eduError && <div className="Login-Error"><AlertTriangle size={14} /> {eduError}</div>}
        <div className="PProfile-Field-Grid">
          <PField label="College"><input value={eduForm.college_name} onChange={(e) => setEduForm({ ...eduForm, college_name: e.target.value })} /></PField>
          <PField label="Year of Graduation"><input type="number" min="2000" max="2030" value={eduForm.year_of_graduation} onChange={(e) => setEduForm({ ...eduForm, year_of_graduation: e.target.value })} /></PField>
        </div>
        <div className="PProfile-Section-Actions">
          <button className="PProfile-Save-Btn" onClick={saveEdu} disabled={eduBusy}>{eduBusy ? "Saving…" : "Save changes"}</button>
        </div>
      </PSection>

      <PSection title="Pay" subtitle="Visible here, changed only through Finance" icon={<Wallet size={15} />} tone="green">
        <div className="PProfile-Field-Grid">
          <PField label="Base Pay"><span className="f-value tabular">{payProfile?.base_pay ? formatMoney(payProfile.base_pay, data.me?.company) : "-"}</span></PField>
          <PField label="Pay Type"><span className="f-value">{payProfile?.pay_type || employee.profile_payload?.pay_type || "-"}</span></PField>
          <PField label="Pay Per Task"><span className="f-value tabular">{payProfile?.pay_per_task ? formatMoney(payProfile.pay_per_task, data.me?.company) : "-"}</span></PField>
          <PField label="Performance Pay"><span className="f-value tabular">{payProfile?.performance_pay ? formatMoney(payProfile.performance_pay, data.me?.company) : "-"}</span></PField>
        </div>
      </PSection>

      <PSection title="Account & Session" subtitle="Username, workspace, password" icon={<KeyRound size={15} />}>
        <div className="PProfile-Field-Grid">
          <PField label="Username"><span className="f-value">{user.username || employee.username || "-"}</span></PField>
          {/* Deliberately NOT the workspace login address (username@banao.tech).
              That string is a sign-in credential shaped like an email, not a
              mailbox — showing it here made everyone (people and AI alike)
              treat it as one. Belonging to the organisation is the fact worth
              showing, the word, not a fake address. (Decision: Saurabh,
              2026-08-05.) Company Email itself is never shown on this page at
              all (2026-10-07) — most people don't have one, and anyone who
              does already knows it from their connected calendar. */}
          <PField label="Organisation"><span className="f-value">{employee.account ? (data.me?.company?.brandName || data.me?.company?.displayName || "-") : "-"}</span></PField>
          <PField label="Tenant"><span className="f-value">{data.me?.activeTenant?.name || data.me?.tenant?.name || data.me?.tenant || "Default Tenant"}</span></PField>
          <PField label="Workspace"><span className="f-value">{data.me?.activeWorkspace?.name || data.me?.workspace?.name || data.me?.workspace || "Default Workspace"}</span></PField>
        </div>
        <ChangeOwnPassword />
        {(user.is_superuser || user.is_staff) && navigate && (
          <p className="PProfile-Why">
            Admin:{" "}
            <button type="button" className="PProfile-Link" onClick={() => navigate("/change-password/")}>reset another person's password</button>
          </p>
        )}
      </PSection>

      <PSection title="Resignation" subtitle="Leaving the company — read the process first" icon={<LogOut size={15} />}>
        <ResignationSection navigate={navigate} />
      </PSection>

      {/* The same quiet "Terms"-style link as the sidebar footer — tiny and
          low-contrast on purpose, not another action button. */}
      {navigate && (
        <p style={{ textAlign: "left", marginTop: 4 }}>
          <button
            type="button"
            onClick={() => navigate("/policies/")}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                     fontSize: 11, color: "var(--c-400)" }}
          >
            Policies
          </button>
        </p>
      )}
    </section>
  );
}
